/*
 * © 2023 Snyk Limited
 *
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PRTemplateInvalidPayloadError = exports.FailedToDeletePRTemplateError = exports.FailedToReadPRTemplateError = exports.FailedToCreatePRTemplateError = exports.FailedToGenerateHashError = exports.FailedToLoadCompiledYamlError = exports.FailedToParsePullRequestAttributesError = exports.FailedToCompilePrTemplateError = exports.PullRequestTemplateNotFoundError = exports.FailedToGetPullRequestAttributesError = void 0;
const problem_error_1 = require("../problem-error");
/**
 * @class
 * @name FailedToGetPullRequestAttributesError
 * @description Snyk could not get the custom pull request template attributes, using the given variables and the fetched pr template.
 *
 * See more:
 * - [https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta](https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta)
 * @summary Failed to get pull request attributes
 * @category Fix
 * @param {string} details                         the specific details that causes this error
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class FailedToGetPullRequestAttributesError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance) {
        super({
            title: 'Failed to get pull request attributes',
            type: 'https://docs.snyk.io/more-info/error-catalog#snyk-pr-template-0001',
            status: 500,
            errorCode: 'SNYK-PR-TEMPLATE-0001',
            level: 'error',
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta',
            ] }, additionalData), cause);
        this.name = this.constructor.name;
    }
}
exports.FailedToGetPullRequestAttributesError = FailedToGetPullRequestAttributesError;
/**
 * @class
 * @name PullRequestTemplateNotFoundError
 * @description Could not find pull request template.
 * @summary Not found
 * @category Fix
 * @param {string} details                         the specific details that causes this error
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class PullRequestTemplateNotFoundError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance) {
        super({
            title: 'Not found',
            type: 'https://docs.snyk.io/more-info/error-catalog#snyk-pr-template-0002',
            status: 404,
            errorCode: 'SNYK-PR-TEMPLATE-0002',
            level: 'error',
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause);
        this.name = this.constructor.name;
    }
}
exports.PullRequestTemplateNotFoundError = PullRequestTemplateNotFoundError;
/**
 * @class
 * @name FailedToCompilePrTemplateError
 * @description Could not compile your customize pull request template, using Handlebars compilation and Snyk variables in place.
 *
 * See more:
 * - [https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta](https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta)
 * @summary Failed to compile pull request template
 * @category Fix
 * @param {string} details                         the specific details that causes this error
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class FailedToCompilePrTemplateError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance) {
        super({
            title: 'Failed to compile pull request template',
            type: 'https://docs.snyk.io/more-info/error-catalog#snyk-pr-template-0003',
            status: 400,
            errorCode: 'SNYK-PR-TEMPLATE-0003',
            level: 'error',
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta',
            ] }, additionalData), cause);
        this.name = this.constructor.name;
    }
}
exports.FailedToCompilePrTemplateError = FailedToCompilePrTemplateError;
/**
 * @class
 * @name FailedToParsePullRequestAttributesError
 * @description Snyk could not parse the custom pull request template, using the given variables and assigning them to the fetched pr template.
 *
 * See more:
 * - [https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta](https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta)
 * @summary Failed to parse pull request attributes
 * @category Fix
 * @param {string} details                         the specific details that causes this error
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class FailedToParsePullRequestAttributesError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance) {
        super({
            title: 'Failed to parse pull request attributes',
            type: 'https://docs.snyk.io/more-info/error-catalog#snyk-pr-template-0004',
            status: 500,
            errorCode: 'SNYK-PR-TEMPLATE-0004',
            level: 'error',
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta',
            ] }, additionalData), cause);
        this.name = this.constructor.name;
    }
}
exports.FailedToParsePullRequestAttributesError = FailedToParsePullRequestAttributesError;
/**
 * @class
 * @name FailedToLoadCompiledYamlError
 * @description Could not load YAML file after substituting Snyk variables into the custom PR template.
 *
 * See more:
 * - [https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta](https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta)
 * @summary Failed to load YAML file after substituting Snyk variables
 * @category Fix
 * @param {string} details                         the specific details that causes this error
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class FailedToLoadCompiledYamlError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance) {
        super({
            title: 'Failed to load YAML file after substituting Snyk variables',
            type: 'https://docs.snyk.io/more-info/error-catalog#snyk-pr-template-0005',
            status: 400,
            errorCode: 'SNYK-PR-TEMPLATE-0005',
            level: 'error',
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta',
            ] }, additionalData), cause);
        this.name = this.constructor.name;
    }
}
exports.FailedToLoadCompiledYamlError = FailedToLoadCompiledYamlError;
/**
 * @class
 * @name FailedToGenerateHashError
 * @description Snyk could not generate hash using the customer PR files and projects vulnIds.
 *
 * See more:
 * - [https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta](https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta)
 * @summary Failed to generate hash for custom PR template
 * @category Fix
 * @param {string} details                         the specific details that causes this error
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class FailedToGenerateHashError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance) {
        super({
            title: 'Failed to generate hash for custom PR template',
            type: 'https://docs.snyk.io/more-info/error-catalog#snyk-pr-template-0006',
            status: 500,
            errorCode: 'SNYK-PR-TEMPLATE-0006',
            level: 'error',
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta',
            ] }, additionalData), cause);
        this.name = this.constructor.name;
    }
}
exports.FailedToGenerateHashError = FailedToGenerateHashError;
/**
 * @class
 * @name FailedToCreatePRTemplateError
 * @description Snyk could not create pull request template.
 *
 * See more:
 * - [https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta](https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta)
 * @summary Unable to create pull request template
 * @category Fix
 * @param {string} details                         the specific details that causes this error
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class FailedToCreatePRTemplateError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance) {
        super({
            title: 'Unable to create pull request template',
            type: 'https://docs.snyk.io/more-info/error-catalog#snyk-pr-template-0007',
            status: 500,
            errorCode: 'SNYK-PR-TEMPLATE-0007',
            level: 'error',
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta',
            ] }, additionalData), cause);
        this.name = this.constructor.name;
    }
}
exports.FailedToCreatePRTemplateError = FailedToCreatePRTemplateError;
/**
 * @class
 * @name FailedToReadPRTemplateError
 * @description Snyk could not get pull request template.
 *
 * See more:
 * - [https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta](https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta)
 * @summary Unable to get pull request template
 * @category Fix
 * @param {string} details                         the specific details that causes this error
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class FailedToReadPRTemplateError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance) {
        super({
            title: 'Unable to get pull request template',
            type: 'https://docs.snyk.io/more-info/error-catalog#snyk-pr-template-0008',
            status: 500,
            errorCode: 'SNYK-PR-TEMPLATE-0008',
            level: 'error',
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta',
            ] }, additionalData), cause);
        this.name = this.constructor.name;
    }
}
exports.FailedToReadPRTemplateError = FailedToReadPRTemplateError;
/**
 * @class
 * @name FailedToDeletePRTemplateError
 * @description Snyk could not delete pull request template.
 *
 * See more:
 * - [https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta](https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta)
 * @summary Unable to delete pull request template
 * @category Fix
 * @param {string} details                         the specific details that causes this error
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class FailedToDeletePRTemplateError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance) {
        super({
            title: 'Unable to delete pull request template',
            type: 'https://docs.snyk.io/more-info/error-catalog#snyk-pr-template-0009',
            status: 500,
            errorCode: 'SNYK-PR-TEMPLATE-0009',
            level: 'error',
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/scan-application-code/snyk-open-source/open-source-basics/customize-pr-templates-closed-beta',
            ] }, additionalData), cause);
        this.name = this.constructor.name;
    }
}
exports.FailedToDeletePRTemplateError = FailedToDeletePRTemplateError;
/**
 * @class
 * @name PRTemplateInvalidPayloadError
 * @description The pull request template payload is invalid.
 * @summary Invalid payload
 * @category Fix
 * @param {string} details                         the specific details that causes this error
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class PRTemplateInvalidPayloadError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance) {
        super({
            title: 'Invalid payload',
            type: 'https://docs.snyk.io/more-info/error-catalog#snyk-pr-template-0010',
            status: 400,
            errorCode: 'SNYK-PR-TEMPLATE-0010',
            level: 'error',
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause);
        this.name = this.constructor.name;
    }
}
exports.PRTemplateInvalidPayloadError = PRTemplateInvalidPayloadError;
//# sourceMappingURL=Fix-error-catalog.js.map